// src/components/adresses/intakeforms/OptionsModalContent.jsx

import { useContext, useEffect, useMemo, useState } from "react";
import { db, timestamp } from "../../../firebase/config.js";
import { client } from "../../../hooks/Client";
import { SavedIcon } from "../../../StateManagment/SavedIcon";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import uuid from "react-uuid";
import { useFirestoreGeneralOrderBy } from "../../../firebase/useFirestore";
import Table from "../../common/table/Table.jsx";
import TableCell from "../../common/table/TableCell.jsx";
import TableDragHandleCell from "../../common/table/TableDragHandleCell.jsx";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";
import {
  OTHER_OPTION_VALUE,
  OTHER_OPTION_LABEL,
  isOtherOption,
  hasOtherOption,
} from "../../../helpers/multipleOptions/otherOption";

/**
 * De antwoordopties van één keuzeveld.
 *
 * @param {object} props
 * @param {{ID: string, Title?: string}} props.field - Het veld (of de matrixkolom) waar de opties bij horen
 * @param {boolean} [props.allowOther] - Of er een 'Anders, namelijk...'-optie bij mag.
 *   STANDAARD UIT, en dat is met opzet: dit venster wordt óók geopend voor de
 *   kolommen van een matrixvraag (IntakeFormMatrixEditor), en een matrixcel
 *   kent geen vrij tekstveld. Zou de knop daar meekomen, dan zet de bouwer een
 *   optie klaar die de invuller nooit te zien krijgt — een belofte die alleen
 *   op één van de twee schermen waargemaakt wordt.
 *   AI-CONTEXT: See docs/pitfalls.md#ui-shared-affordance-gated-per-caller
 */
const OptionsModalContent = ({ field, allowOther = false }) => {
  const [, setSaved] = useContext(SavedIcon);

  const fieldId = field?.ID || "";

  // Fetch ALL options for this field (we’ll filter out soft-deleted in UI)
  const rawOptions = useFirestoreGeneralOrderBy(
    "MultipleQuestionOptions",
    "Field",
    fieldId,
    "Position",
    "asc"
  );

  const activeOptions = useMemo(() => {
    return (rawOptions || []).filter((o) => (o?.Deleted || "false") !== "true");
  }, [rawOptions]);

  const [dragArrayOptions, setDragArrayOptions] = useState([]);

  useEffect(() => {
    setDragArrayOptions(activeOptions || []);
  }, [activeOptions]);

  /**
   * @param {string} [option] - Beginwaarde; `OTHER_OPTION_VALUE` maakt er de
   *   'Anders, namelijk...'-optie van, leeg een gewone optie om zelf in te typen.
   */
  const addOption = async (option = "") => {
    if (!field?.ID) return;

    try {
      await db.collection("MultipleQuestionOptions").add({
        Option: option,
        Question: field?.Title || "",
        Field: field.ID,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Position: (activeOptions?.length || 0) + 1,
        ID: uuid(),
        Deleted: "false",
        DeletedAt: "",
      });

      setSaved("flex");
    } catch (err) {
      console.error("Failed to add option:", err);
    }
  };

  const onChangeOption = async (docid, value) => {
    if (!docid) return;

    try {
      await db.collection("MultipleQuestionOptions").doc(docid).update({
        Option: value,
        // keep Question synced with field title (handy for legacy screens)
        Question: field?.Title || "",
      });
      setSaved("flex");
    } catch (err) {
      console.error("Failed to update option:", err);
    }
  };

  // ✅ soft delete (matches your counting logic + housing auto logic)
  const onDeleteOption = async (docid) => {
    if (!docid) return;

    try {
      await db.collection("MultipleQuestionOptions").doc(docid).update({
        Deleted: "true",
        DeletedAt: timestamp,
      });
      setSaved("flex");
    } catch (err) {
      console.error("Failed to soft delete option:", err);
    }
  };

  const persistReorder = async (items) => {
    if (!items?.length) return;

    try {
      await Promise.all(
        items.map((opt, idx) =>
          db.collection("MultipleQuestionOptions")
            .doc(opt.docid)
            .update({ Position: idx + 1 })
        )
      );
      setSaved("flex");
    } catch (err) {
      console.error("Failed to reorder options:", err);
    }
  };

  const handleDragEndOptions = async (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayOptions);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setDragArrayOptions(items);
    await persistReorder(items);
  };

  if (!field?.ID) {
    return <p style={{ opacity: 0.7 }}>Geen veld geselecteerd.</p>;
  }

  return (
    <div style={{ marginTop: "10px" }}>
      <h3 style={{ marginBottom: "8px" }}>
        {field?.Title || "—"}
      </h3>

      <ReadOnlyGuard edit="forms.edit">
        {(canEdit) => (
          <>
          {/* Eén per vraag: twee vrije tekstvelden onder dezelfde vraag zijn voor
              de invuller onnavolgbaar, en de antwoorden zijn daarna niet meer uit
              elkaar te houden. */}
          {allowOther && canEdit && !hasOtherOption(dragArrayOptions) && (
            <button
              type="button"
              className="data-field-options-button"
              onClick={() => addOption(OTHER_OPTION_VALUE)}
            >
              {`${OTHER_OPTION_LABEL} toevoegen`}
            </button>
          )}
          <Table
            columns={[
              { key: 'drag', label: '', searchable: false },
              { key: 'option', label: 'OPTIE' },
              { key: 'delete', label: 'VERWIJDEREN', searchable: false },
            ]}
            items={dragArrayOptions || []}
            draggable
            droppableId={`options-${field.ID}`}
            onDragEnd={handleDragEndOptions}
            getItemKey={(opt) => opt.docid}
            showSearch={false}
            emptyMessage="Geen opties gevonden."
            addRow={canEdit ? {
              toolTipContent: 'Optie toevoegen',
              // Niet `onClick: addOption`: die krijgt dan het klik-event als
              // eerste argument en zou dat als beginwaarde wegschrijven.
              onClick: () => addOption(),
            } : undefined}
            renderRow={(opt) => (
              <>
                <TableDragHandleCell>
                  <DragIndicatorOutlinedIcon />
                </TableDragHandleCell>
                <TableCell>
                  {/* De tekst van deze optie levert de invuller zelf, dus hier
                      valt niets in te typen. */}
                  {isOtherOption(opt) ? (
                    <p className="intake-other-option">{OTHER_OPTION_LABEL}</p>
                  ) : (
                    <input
                      type="text"
                      defaultValue={opt.Option}
                      onBlur={(e) => onChangeOption(opt.docid, e.target.value)}
                      placeholder="Typ een optie"
                      autoComplete="off"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <DeleteOutlinedIcon
                      className="table-delete-icon"
                      onClick={() => onDeleteOption(opt.docid)}
                    />
                  )}
                </TableCell>
              </>
            )}
          />
          </>
        )}
      </ReadOnlyGuard>
    </div>
  );
};

export default OptionsModalContent;
